/**
 * Behavior — a utility-scored behaviour tree.
 *
 * The root is a priority selector over four groups (death, reflex, combat,
 * search) and inside the combat group is a utility selector: every behaviour
 * declares an entry condition, a score, a minimum dwell time and an exit
 * cooldown, and the runner picks the best legal one with hysteresis so the
 * agent commits to a decision instead of twitching between two.
 *
 * Behaviours never touch physics or the nav graph directly. They drive the
 * agent's intent API (`moveTo`, `holdPosition`, `aimAt`, `setFireIntent`,
 * `requestCover`, `callout`) and the agent turns intent into motion.
 *
 * owner: ai
 */
import * as THREE from 'three';
import { ALERT_SUSPICIOUS, ALERT_ALERT, ALERT_COMBAT } from './Perception.js';
import { ROLE_AGGRESSOR, ROLE_FLANKER, ROLE_GRENADIER, ROLE_SUPPRESS } from './Squad.js';

const _v = new THREE.Vector3();
const _p = new THREE.Vector3();

/** A behaviour: entry condition, utility, dwell time, exit cooldown. */
class Behaviour {
  constructor(spec) {
    this.name = spec.name;
    this.group = spec.group || 'combat';
    this.minTime = spec.minTime ?? 0.4;
    this.maxTime = spec.maxTime ?? Infinity;
    this.cooldown = spec.cooldown ?? 0;
    this.interruptible = spec.interruptible !== false;
    this._can = spec.can || (() => true);
    this._score = spec.score || (() => 0);
    this._enter = spec.enter || null;
    this._tick = spec.tick || null;
    this._exit = spec.exit || null;
  }
  can(a) { try { return !!this._can(a); } catch { return false; } }
  score(a) { try { return this._score(a); } catch { return -Infinity; } }
  enter(a) { if (this._enter) this._enter(a); }
  exit(a) { if (this._exit) this._exit(a); }
  tick(a, dt) { return this._tick ? this._tick(a, dt) : true; }
}

const B = (spec) => new Behaviour(spec);

/* ------------------------------------------------------------------ *
 * helpers
 * ------------------------------------------------------------------ */
const losToPlayer = (a) => a.perception.hasLOS;
const combat = (a) => a.perception.alert >= ALERT_COMBAT || (a.squad && a.squad.engaged);
const distToBelief = (a) => a.pos.distanceTo(a.beliefPos());
const healthFrac = (a) => a.health / a.maxHealth;

/* ------------------------------------------------------------------ *
 * the library
 * ------------------------------------------------------------------ */

export const BEHAVIOURS = [

  /* ---------------- death ---------------- */
  B({
    name: 'death', group: 'death', minTime: 999, interruptible: false,
    can: (a) => !a.alive,
    score: () => 1e6,
    enter: (a) => { a.stopMoving(); a.setFireIntent('hold'); },
    tick: () => true,
  }),

  /* ---------------- reflexes ---------------- */
  B({
    name: 'panic', group: 'reflex', minTime: 1.4, maxTime: 3.2, cooldown: 14,
    can: (a) => a.alive && healthFrac(a) < 0.34 && a.perception.alert >= ALERT_ALERT
      && !a.inCover && distToBelief(a) < 16,
    score: (a) => 900 + (1 - healthFrac(a)) * 100,
    enter: (a) => {
      a.callout('panic');
      a.playAnim('panic');
      a.setStance('stand');
      const n = a.requestCover({ away: true, radius: 11, minDist: 3 });
      if (n < 0) a.moveAwayFrom(a.beliefPos(), 8);
      a.setFireIntent('hold');
      a.sprint = true;
    },
    tick: (a) => { a.faceMove(); return !a.arrived(); },
    exit: (a) => { a.sprint = false; },
  }),

  B({
    name: 'reload_in_cover', group: 'reflex', minTime: 0.2, maxTime: 6,
    can: (a) => a.alive && a.needsReload() && !a.reloading,
    score: (a) => a.ammo === 0 ? 850 : (a.inCover && !losToPlayer(a) ? 620 : 240),
    enter: (a) => {
      a.setFireIntent('hold');
      if (a.inCover) {
        a.stopMoving();
        a.setStance('crouch');
        a.callout('reloading');
        a.beginReload();
      } else {
        const n = a.requestCover({ radius: 9 });
        if (n < 0) { a.callout('reloading'); a.beginReload(); }
      }
    },
    tick: (a) => {
      if (!a.reloading && a.inCover) { a.callout('reloading'); a.beginReload(); }
      if (!a.reloading && a.arrived()) { a.callout('reloading'); a.beginReload(); }
      a.faceTarget();
      return a.reloading || !a.arrived();
    },
    exit: (a) => { a.setStance('stand'); },
  }),

  B({
    name: 'throw_grenade', group: 'reflex', minTime: 1.6, maxTime: 3.4, cooldown: 20,
    can: (a) => a.alive && a.role === ROLE_GRENADIER && a.canThrowGrenade()
      && a.squad && a.squad.confidence > 0.6
      && distToBelief(a) > 7 && distToBelief(a) < 26,
    score: () => 700,
    enter: (a) => {
      a.stopMoving();
      a.setStance('stand');
      a.setFireIntent('hold');
      a.callout('grenade');
      a.playAnim('throw');
      a.grenadeTimer = 0.85;      // telegraph before the release
    },
    tick: (a, dt) => {
      a.faceTarget();
      a.grenadeTimer -= dt;
      if (a.grenadeTimer <= 0 && !a.grenadeThrown) { a.throwGrenade(); return true; }
      return a.grenadeTimer > -0.5;
    },
    exit: (a) => { a.grenadeThrown = false; a.role = ROLE_SUPPRESS; },
  }),

  /* ---------------- combat ---------------- */
  B({
    name: 'fire_from_cover', group: 'combat', minTime: 1.1, maxTime: 5.5,
    can: (a) => a.alive && combat(a) && a.inCover && a.ammo > 0
      && a.squad && a.squad.confidence > 0.35
      && (losToPlayer(a) || a.canPeekTarget()),
    score: (a) => 480 + (losToPlayer(a) ? 60 : 0) - a.suppressedBy * 120,
    enter: (a) => {
      a.stopMoving();
      a.setStance(a.coverIsHigh ? 'stand' : 'crouch');
      a.setFireIntent('aimed');
      a.beginPeek();
    },
    tick: (a, dt) => {
      a.faceTarget();
      a.tickPeek(dt);
      return a.inCover && a.ammo > 0;
    },
    exit: (a) => { a.endPeek(); a.setFireIntent('hold'); },
  }),

  B({
    name: 'suppress', group: 'combat', minTime: 1.6, maxTime: 6.0,
    can: (a) => a.alive && combat(a) && a.ammo > 3
      && (a.role === ROLE_SUPPRESS || a.role === 'hold')
      && a.squad && a.squad.confidence > 0.3,
    score: (a) => 430 - a.suppressedBy * 90 + (a.inCover ? 40 : 0),
    enter: (a) => {
      a.stopMoving();
      // Only a man behind something chest-high stands to shoot. Behind a low wall
      // he is crouched, and caught in the open he is on a knee — the readable
      // silhouette difference between "in cover" and "standing in a car park".
      a.setStance(a.inCover && a.coverIsHigh ? 'stand' : 'crouch');
      a.setFireIntent('suppress');
      a.callout('suppressing');
      a.beginPeek();
    },
    tick: (a, dt) => { a.faceTarget(); a.tickPeek(dt); return a.ammo > 0; },
    exit: (a) => { a.endPeek(); a.setFireIntent('hold'); },
  }),

  B({
    name: 'advance', group: 'combat', minTime: 1.4, maxTime: 7.0, cooldown: 2.5,
    can: (a) => a.alive && combat(a) && a.role === ROLE_AGGRESSOR
      && a.squad && a.squad.confidence > 0.4 && distToBelief(a) > 6.5,
    score: (a) => 520 - a.suppressedBy * 200,
    enter: (a) => {
      a.callout('moving');
      a.setStance('stand');
      a.setFireIntent('hold');
      const n = a.requestCover({ radius: 9, towards: true, minDist: 3.5 });
      if (n < 0) a.moveTo(a.beliefPos(), 0.85);
      a.playAnim('run');
    },
    tick: (a) => { a.faceMove(); return !a.arrived(); },
    exit: (a) => { a.playAnim('idle'); },
  }),

  B({
    name: 'flank', group: 'combat', minTime: 2.0, maxTime: 14.0, cooldown: 4,
    can: (a) => a.alive && combat(a) && a.role === ROLE_FLANKER && a.flankNode >= 0 && !a.flankDone,
    score: () => 500,
    enter: (a) => {
      a.setStance('stand');
      a.setFireIntent('hold');
      a.moveToNode(a.flankNode, 0.95);
      a.playAnim('run');
    },
    tick: (a) => {
      a.faceMove();
      if (a.arrived()) { a.flankDone = true; return false; }
      return true;
    },
    exit: (a) => { a.playAnim('idle'); },
  }),

  B({
    name: 'reposition', group: 'combat', minTime: 1.0, maxTime: 4.5, cooldown: 5.5,
    can: (a) => a.alive && combat(a) && a.inCover && a.coverStale > 5.5 && a.suppressedBy > 0.35,
    score: (a) => 460 + a.suppressedBy * 90,
    enter: (a) => {
      a.setStance('crouch');
      a.setFireIntent('hold');
      const n = a.requestCover({ radius: 8, lateral: true, minDist: 2.2 });
      if (n < 0) a.moveAwayFrom(a.beliefPos(), 4);
      a.playAnim('run');
    },
    tick: (a) => { a.faceMove(); return !a.arrived(); },
    exit: (a) => { a.setStance('stand'); a.playAnim('idle'); },
  }),

  B({
    name: 'take_cover', group: 'combat', minTime: 0.8, maxTime: 6.0,
    can: (a) => a.alive && combat(a) && !a.inCover,
    score: (a) => 470 + (losToPlayer(a) ? 80 : 0),
    enter: (a) => {
      a.setStance('stand');
      a.setFireIntent('hold');
      const n = a.requestCover({ radius: 9 });
      a.coverSearchFailed = n < 0;
      if (n < 0) {
        // Nowhere within reach. A soldier caught in the open goes to a knee and
        // returns fire; he does not stand at full height jogging backwards,
        // which is what the old `moveAwayFrom` produced in every squad shot.
        a.stopMoving();
        a.setStance('crouch');
        a.setFireIntent('aimed');
      }
      a.playAnim(n < 0 ? 'idle' : 'run');
    },
    tick: (a) => {
      a.faceTarget();
      if (a.coverSearchFailed) return true;    // maxTime ends it and it retries
      return !a.arrived();
    },
    exit: (a) => { a.coverSearchFailed = false; a.setStance('stand'); a.playAnim('idle'); },
  }),

  B({
    name: 'retreat', group: 'combat', minTime: 1.2, maxTime: 5.0, cooldown: 9,
    can: (a) => a.alive && combat(a) && healthFrac(a) < 0.45 && distToBelief(a) < 11,
    score: (a) => 560 * (1 - healthFrac(a)),
    enter: (a) => {
      a.callout('falling_back');
      a.setStance('stand');
      a.setFireIntent('hold');
      const n = a.requestCover({ away: true, radius: 14, minDist: 5 });
      if (n < 0) a.moveAwayFrom(a.beliefPos(), 9);
      a.playAnim('run');
    },
    tick: (a) => { a.faceMove(); return !a.arrived(); },
    exit: (a) => { a.playAnim('idle'); },
  }),

  B({
    name: 'flush_out', group: 'combat', minTime: 1.5, maxTime: 6.0, cooldown: 12,
    can: (a) => a.alive && combat(a) && a.role === ROLE_AGGRESSOR
      && a.squad && a.squad.time - a.squad.lastContact > 5.0 && a.squad.confidence > 0.3,
    score: () => 540,
    enter: (a) => {
      a.callout('flushing');
      a.setStance('stand');
      a.setFireIntent('hold');
      const n = a.requestCover({ radius: 10, towards: true, sightOn: true, minDist: 3 });
      if (n < 0) a.moveTo(a.beliefPos(), 0.8);
      a.playAnim('run');
    },
    tick: (a) => { a.faceMove(); return !a.arrived(); },
    exit: (a) => { a.playAnim('idle'); },
  }),

  /* ---------------- search ---------------- */
  B({
    name: 'investigate', group: 'search', minTime: 1.5, maxTime: 12.0,
    can: (a) => a.alive && a.perception.alert >= ALERT_ALERT && a.perception.everSeen,
    score: (a) => 300 + a.perception.confidence * 60,
    enter: (a) => {
      a.callout('contact_lost');
      a.setStance('stand');
      a.setFireIntent('hold');
      a.perception.searchPoint(_p);
      a.moveTo(_p, 0.65);
      a.searchLook = 0;
      a.playAnim('walk');
    },
    tick: (a, dt) => {
      a.searchLook += dt;
      if (a.arrived()) {
        // sweep the muzzle across likely angles rather than staring at a wall
        const sweep = Math.sin(a.searchLook * 1.3) * 0.9;
        a.beliefPos(_v);
        _v.x += Math.cos(a.searchLook * 0.7) * 4 * sweep;
        _v.z += Math.sin(a.searchLook * 0.7) * 4 * sweep;
        a.aimAt(_v);
        if (a.searchLook > 5.5) {
          a.perception.confidence *= 0.4;
          return false;
        }
        return true;
      }
      a.faceMove();
      return true;
    },
    exit: (a) => { a.playAnim('idle'); },
  }),

  B({
    name: 'alerted', group: 'search', minTime: 1.0, maxTime: 5.0,
    can: (a) => a.alive && a.perception.alert >= ALERT_SUSPICIOUS,
    score: () => 250,
    enter: (a) => {
      a.stopMoving();
      a.setStance('stand');
      a.setFireIntent('hold');
      a.callout('what_was_that');
      a.playAnim('alert');
    },
    tick: (a) => {
      if (a.perception.soundStrength > 0.05) a.aimAt(a.perception.soundPos);
      else a.faceTarget();
      return a.perception.alert >= ALERT_SUSPICIOUS;
    },
  }),

  /* ---------------- idle ---------------- */
  B({
    name: 'patrol', group: 'idle', minTime: 2.0, maxTime: 25.0,
    can: (a) => a.alive && a.patrolPoints.length > 1,
    score: () => 100,
    enter: (a) => {
      a.setStance('stand');
      a.setFireIntent('hold');
      a.nextPatrol();
      a.playAnim('walk');
    },
    tick: (a) => {
      a.faceMove();
      if (a.arrived()) { a.nextPatrol(); }
      return true;
    },
    exit: (a) => { a.playAnim('idle'); },
  }),

  B({
    name: 'idle', group: 'idle', minTime: 1.0, maxTime: 8.0,
    can: (a) => a.alive,
    score: () => 50,
    enter: (a) => {
      a.stopMoving();
      a.setStance('stand');
      a.setFireIntent('hold');
      a.playAnim('idle');
      a.idlePhase = a.rng() * 6.28;
    },
    tick: (a, dt) => {
      a.idlePhase += dt * 0.4;
      a.lookAround(a.idlePhase);
      return true;
    },
  }),
];

const GROUP_PRIORITY = { death: 5, reflex: 4, combat: 3, search: 2, idle: 1 };

/**
 * Runs the library for one agent. Selection is priority-by-group first, then
 * utility inside the winning group, with a stickiness bonus for the behaviour
 * already running so the agent commits.
 */
export class BehaviourRunner {
  constructor(agent) {
    this.agent = agent;
    this.current = null;
    this.elapsed = 0;
    this.cooldowns = new Map();
    this.path = 'idle/idle';
    this.lastScores = [];
  }

  reset() {
    if (this.current) this.current.exit(this.agent);
    this.current = null;
    this.elapsed = 0;
    this.cooldowns.clear();
    this.path = 'idle/idle';
  }

  /** @param {number} dt seconds since this agent's last behaviour slice */
  tick(dt, time) {
    const a = this.agent;

    if (this.current) {
      this.elapsed += dt;
      let alive;
      try { alive = this.current.tick(a, dt); } catch { alive = false; }
      if (alive === false || this.elapsed > this.current.maxTime) this._end(time);
    }

    // re-select when nothing is running, or when the dwell time is satisfied
    const canSwitch = !this.current
      || (this.current.interruptible && this.elapsed >= this.current.minTime);
    if (!canSwitch) return;

    let bestGroup = -1, best = null, bestScore = -Infinity;
    this.lastScores.length = 0;
    for (const b of BEHAVIOURS) {
      const cd = this.cooldowns.get(b.name) ?? -1e9;
      if (time < cd) continue;
      if (!b.can(a)) continue;
      const gp = GROUP_PRIORITY[b.group] ?? 0;
      let s = b.score(a);
      if (b === this.current) s += 45;              // hysteresis
      if (gp < bestGroup) continue;
      if (gp > bestGroup || s > bestScore) { bestGroup = gp; bestScore = s; best = b; }
    }

    if (!best || best === this.current) return;
    this._end(time);
    this.current = best;
    this.elapsed = 0;
    this.path = `${best.group}/${best.name}`;
    try { best.enter(a); } catch { /* a bad enter must not stall the agent */ }
  }

  _end(time) {
    if (!this.current) return;
    try { this.current.exit(this.agent); } catch { /* ignore */ }
    if (this.current.cooldown > 0) {
      this.cooldowns.set(this.current.name, time + this.current.cooldown);
    }
    this.current = null;
    this.elapsed = 0;
  }

  get name() { return this.current ? this.current.name : 'none'; }
}
